import React from "react";
import { Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import { Stack } from "components/layout/reusableLayouts";
import Summary, { SummaryItem } from "components/common/Summary";
import { useShippingServiceTooltip } from "components/common/cards/shippingServiceTooltip";

export type ShippingStatus =
  | "canceled"
  | "delivered"
  | "failure"
  | "pending"
  | "returned"
  | "shipped"
  | "submitted";

export interface ShippingAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface CardShipping {
  name: string;
  address: ShippingAddress;
  carrier: string;
  status: ShippingStatus;
  eta?: number | null;
  shippingError?: string | null;
  trackingNumber?: string | null;
  trackingUrl?: string | null;
}

const statusDescriptions: Record<CardShipping["status"], string> = {
  canceled: "PhysicalCardCanceledStatusDescription",
  delivered: "PhysicalCardDeliveredStatusDescription",
  failure: "PhysicalCardFailureStatusDescription",
  pending: "PhysicalCardPendingStatusDescription",
  returned: "PhysicalCardReturnedStatusDescription",
  shipped: "PhysicalCardShippedStatusDescription",
  submitted: "PhysicalCardSubmittedStatusDescription",
};

const CardShippingDetails: React.FC<{
  shipping: CardShipping;
  shippingSpeed?: string | null;
}> = ({ shipping, shippingSpeed }) => {
  const { t } = useTranslation();
  const shippingServiceTooltip = useShippingServiceTooltip();
  const getStatusLabel = (value: ShippingStatus) =>
    t(value, { defaultValue: titleCase(value) });
  if (!shipping) return null;
  const { address, carrier, eta, name, status, shippingError } = shipping;

  const items: SummaryItem[] = [
    { title: t("Recipient"), value: name },
    {
      title: t("Address"),
      // Two lines of address read badly in half a column.
      fullWidth: true,
      value: address?.line1 ? (
        <>
          <div>
            {address.line1}
            {address.line2 ? `, ${address.line2}` : ""}
          </div>
          <div>
            {address.city}, {address.state} {address.postal_code},{" "}
            {address.country}
          </div>
        </>
      ) : null,
    },
    { title: t("Carrier"), value: carrier ? titleCase(carrier) : null },
    {
      title: t("Service"),
      tooltip: shippingServiceTooltip,
      value: shippingSpeed ? t(shippingSpeed) : null,
    },
    {
      title: t("Status"),
      tooltip: (
        <Stack gap="var(--space-2)">
          {Object.entries(statusDescriptions).map(([key, desc]) => (
            <Typography key={key} variant="body2" component="div">
              <strong>{getStatusLabel(key as ShippingStatus)}:</strong>{" "}
              {t(desc)}
            </Typography>
          ))}
        </Stack>
      ),
      value: status ? getStatusLabel(status) : null,
    },
    {
      title: t("cardTrackingNumber"),
      value: shipping.trackingNumber ? (
        shipping.trackingUrl ? (
          <Link
            href={shipping.trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {shipping.trackingNumber}
          </Link>
        ) : (
          shipping.trackingNumber
        )
      ) : null,
    },
    {
      title: t("ETA"),
      value: eta ? new Date(eta * 1000).toLocaleDateString() : null,
    },
    {
      // Summary has no tone, and this is its only would-be caller, so the
      // colour rides on the nodes rather than becoming a fourth prop.
      title: (
        <Typography
          variant="caption"
          component="span"
          sx={{ color: "var(--color-text-danger)" }}
        >
          {t("Shipping Error")}
        </Typography>
      ),
      fullWidth: true,
      value: shippingError ? (
        <Typography
          variant="body1"
          component="span"
          sx={{ color: "var(--color-text-danger)" }}
        >
          {shippingError}
        </Typography>
      ) : null,
    },
  ];

  return <Summary items={items} disableContainer />;
};

export default CardShippingDetails;
